import type { CSSProperties } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { SPRING_SNAPPY, tapScale } from '../lib/motion';
import { Icon } from './Icon';
import { PARENT_PORTAL_URL } from '../lib/parentPortal';

const MotionAnchor = motion.create('a');

const buttonStyle: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 'var(--space-2)',
  width: '100%',
  minHeight: 'var(--tap-target)',
  padding: '0 var(--space-5)',
  borderRadius: 'var(--radius-md)',
  border: '1px solid var(--silver-soft)',
  background: 'linear-gradient(180deg, var(--paper) 0%, var(--silver-panel) 100%)',
  color: 'var(--ink)',
  boxShadow:
    'inset 0 1px 0 rgba(255,255,255,0.65), 0 1px 0 var(--silver-edge), 0 8px 16px rgba(35,28,20,0.10)',
  fontFamily: 'var(--font-body)',
  fontSize: 'var(--text-body-l)',
  fontWeight: 800,
  lineHeight: 1,
  textDecoration: 'none',
  cursor: 'pointer',
};

/**
 * The one parent portal button, mounted on every signed-in home so every
 * role can find it (Bobbie, 2026-09-07, then renamed the same day: "Sorry
 * name it the parent portal... they know what that is"). Plain external
 * link styled as a Chalk secondary button - the studio's own tokens carry
 * the look (see Button.tsx's secondary variant, DESIGN.md section 8), same
 * shape everywhere it appears so it never reads as a different app.
 *
 * Copy is ONLY the label. Nothing about what the portal is used for is
 * invented here - Bobbie gave us only the link and the name. The new-tab
 * cue lives in the aria-label alone (independent review on PR #1033, P3: an
 * earlier optional flag prop, off by default with no caller ever turning it
 * on, gated a second line of copy that could never actually render -
 * removed along with the flag rather than left as dead code).
 */
export function ParentPortalButton({
  className,
  style,
}: {
  className?: string;
  style?: CSSProperties;
}) {
  const reduce = useReducedMotion();
  return (
    <MotionAnchor
      href={PARENT_PORTAL_URL}
      target="_blank"
      rel="noopener noreferrer"
      aria-label="Parent portal, opens in a new tab"
      className={['ldg-btn', className].filter(Boolean).join(' ')}
      data-variant="secondary"
      whileTap={tapScale(reduce)}
      transition={SPRING_SNAPPY}
      style={{ ...buttonStyle, ...style }}
    >
      <Icon name="link" size={16} />
      Parent portal
    </MotionAnchor>
  );
}

export default ParentPortalButton;
